import type { FC, PropsWithChildren, ReactNode } from 'react';

import { DialogContent, DialogOverlay } from '@reach/dialog';
import { useRouter } from 'next/compat/router';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import isEqual from 'react-fast-compare';
import { CSSTransition } from 'react-transition-group';
import styled, { css } from 'styled-components';

import { SideNavItem } from './SideNavItem';
import { colors, media } from '../../core/styles';
import { sideNavTransitionProperties } from '../../sideNavAnimationStyles';
import { isAccountNavigationNode } from '../accountMenu/accountMenuData';
import { PageTypes } from '~/utilities/constants';
import { useMediaQueryContext } from '~/utilities/context/dynamic/MediaQueryContext';
import { useNavActionContext } from '~/utilities/context/dynamic/NavActionContext';
import { useSearchContext } from '~/utilities/context/dynamic/SearchContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import { SiteType } from '~/utilities/graphql/codegen';
import { getActiveNavItems, getSiteStyles } from '~/utilities/helpers';
import { useTopNavTracking } from '~/utilities/hooks/useTopNavTracking';

const S = {
  SideNavContainer: styled.ul`
    list-style: none;
    appearance: none;
    margin: 0;
    padding: 0;
    ${({ theme }) => css`
      background-color: ${theme.colors.background.base};
      border-right: ${theme.borders.width.xs} solid ${theme.colors.border.subtle};
      box-sizing: border-box;
      height: 100%;
      max-height: 100%;
      position: relative;
      padding-bottom: 54px;
      overflow: hidden;

      &:hover,
      &:focus {
        overflow-y: auto;
      }

      @media ${media(theme).lessThan('sm')} {
        overflow-y: scroll;
      }

      ${getSiteStyles(theme.siteType, {
        [SiteType.Aaf]: css`
          border: none;
        `,
      })}
    `}
  `,

  BottomPanel: styled.li`
    ${({ theme }) => css`
      position: fixed;
      bottom: 0;
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      background: ${theme.colors.background.subtle};
      padding: ${`${theme.padding.none} ${theme.padding.sm}`};
      box-sizing: border-box;
      border-top: ${theme.borders.width.xs} solid ${theme.colors.border.subtle};

      @media ${media(theme).greaterThan('sm')} {
        display: none;
      }

      ${getSiteStyles(theme.siteType, {
        [SiteType.Outlet]: css`
          background-color: ${theme.colors.background.base};
          border-color: ${theme.colors.border.inverse};
        `,
      })}
    `}
  `,

  SideNavBackdrop: styled.div<{ $sideNavOpen: boolean }>`
    ${({ theme, $sideNavOpen }) => css`
      display: block;
      opacity: ${$sideNavOpen ? 0.5 : 0};
      background: ${colors.BLACK};
      z-index: 502;
      position: fixed;
      inset: 0;
      visibility: ${$sideNavOpen ? 'visible' : 'hidden'};
      ${sideNavTransitionProperties(['opacity', 'visibility'])}

      @media ${media(theme).greaterThan('xl')} {
        display: none;
      }
    `}
  `,

  SideNav: styled.nav<{ $sideNavOpen: boolean; $isSearchBoxOpen: boolean }>`
    ${({ theme, $sideNavOpen, $isSearchBoxOpen }) => css`
      position: fixed;
      inset: 0;
      margin-top: calc(var(--smart-banner-height));
      transform: translate3d(${$sideNavOpen ? 0 : '-100%'}, 0, 0);
      visibility: ${$sideNavOpen ? 'visible' : 'hidden'};
      z-index: ${$isSearchBoxOpen ? 502 : 503};
      width: var(--side-nav-max-width);
      ${sideNavTransitionProperties(['transform', 'visibility'])}

      @media ${media(theme).greaterThan('sm')} {
        margin-top: calc(var(--top-nav-height) + var(--smart-banner-height));
      }

      @media ${media(theme).greaterThan('xl')} {
        right: auto;
        background: ${colors.WHITE};

        .has-open-sideNavigation & {
          transform: translate3d(0, 0, 0);
          visibility: visible;
        }
      }
    `}

    ${({ theme }) => css`
      @media ${media(theme).lessThan('xl')} {
        display: none;
      }
    `}
  `,

  DialogSidenav: styled(DialogContent)`
    background-color: ${({ theme }) => theme.colors.background.base};
    position: fixed;
    inset: 0;
    margin-top: calc(var(--smart-banner-height));
    width: var(--side-nav-max-width);
    z-index: 505;
    opacity: 0;
    transform: translateX(-100%);
    padding-bottom: 57px; /* size of the login button stuck to the bottom */

    ${({ theme }) => css`
      @media ${media(theme).greaterThan('sm')} {
        margin-top: calc(var(--top-nav-height) + var(--smart-banner-height));
      }
    `}

    &.sidenav-dialog {
      &-enter {
        opacity: 0;
        transform: translateX(-100%);
      }

      &-enter-active {
        opacity: 1;
        transform: translateX(0);

        @media (prefers-reduced-motion: no-preference) {
          transition-duration: 250ms;
          transition-timing-function: ease-in-out;
          transition-property: opacity, transform;
        }
      }

      &-enter-done {
        opacity: 1;
        transform: translateX(0);
      }

      &-exit {
        opacity: 1;
        transform: translateX(0);
      }

      &-exit-active {
        opacity: 0;
        transform: translateX(-100%);

        @media (prefers-reduced-motion: no-preference) {
          transition-duration: 250ms;
          transition-timing-function: ease-in-out;
          transition-property: opacity, transform;
        }
      }
    }
  `,

  DialogSidenavCloseButton: styled.button`
    appearance: none;
    height: var(--top-nav-height);
    aspect-ratio: 1;
    background-color: transparent;
    position: absolute;
    top: 0;
    right: 0;
    transform: translateX(100%);

    ${({ theme }) => css`
      @media ${media(theme).greaterThan('sm')} {
        left: 0;
        right: unset;
        transform: translateY(-100%);
      }
    `}
  `,

  MobileNavWrapper: styled.nav`
    height: 100%;
  `,
};

type SideNavigationProps = PropsWithChildren<{
  header?: ReactNode;
  /** Below desktop the mobile TLN drawer replaces the side navigation dialog. */
  mobileDialogDisabled?: boolean;
}>;

export const SideNavigationComponent: FC<SideNavigationProps> = ({
  children,
  header,
  mobileDialogDisabled = false,
}) => {
  const router = useRouter();
  const { locale } = useAppContext();
  const {
    pageType,
    leftNavigation,
    accessibilityLabels: { closenSideBar } = {},
  } = useStaticContext();
  const { isLargeDesktop } = useMediaQueryContext();
  const { sideNavOpened, closeSideNav } = useNavActionContext();
  const { isSearchBoxOpen } = useSearchContext();
  const { track } = useTopNavTracking();

  const isSmallScreens = !isLargeDesktop;

  const dialogRef = useRef<HTMLDivElement>(null);
  const navElementRef = useRef<HTMLUListElement>(null);

  const sideNavRef = useRef<HTMLElement>(null);

  const [currentPath] = (router?.asPath ?? '').split('?') || [];
  const currentPathWithoutProduct =
    pageType === PageTypes.PRODUCT ? currentPath.split('/').slice(0, -1).join('/') : currentPath;
  const currentCategoryURL = `/${locale}${currentPathWithoutProduct}`;

  const activeNodeInfo = useMemo(
    () => getActiveNavItems(leftNavigation, currentCategoryURL),
    [currentCategoryURL, leftNavigation]
  );
  const [activeIndexArray, setActiveIndexArray] = useState<string[]>([]);
  const activeItemUrl = activeNodeInfo?.navItem?.url;

  useEffect(() => {
    if (activeNodeInfo?.indexArray) {
      setActiveIndexArray(activeNodeInfo.indexArray);
    }
  }, [activeNodeInfo?.indexArray]);

  useEffect(() => {
    if (sideNavRef.current && isLargeDesktop) {
      sideNavRef.current.ontransitionend = () => {
        window.dispatchEvent(new Event('resize'));
      };
    }
  }, [sideNavOpened, isLargeDesktop]);

  const ariaHiddenAttribute = isSmallScreens ? true : !sideNavOpened;

  useEffect(() => {
    if (sideNavRef.current) {
      sideNavRef.current.setAttribute('aria-hidden', String(ariaHiddenAttribute));
    }
  }, [ariaHiddenAttribute]);

  const content = (
    <>
      {header}

      <S.SideNavContainer ref={navElementRef} data-cs-capture="">
        {leftNavigation?.navigation?.map((item, index) => (
          <SideNavItem
            {...item}
            key={item?.uid || index}
            depthLevel={0}
            labelPath={[]}
            navElement={isAccountNavigationNode(item) ? 'account' : 'nav_item'}
            activeIndexArray={activeIndexArray}
            currentIndex={`${index}`}
            updateActiveItem={setActiveIndexArray}
            activeItemUrl={activeItemUrl}
          />
        ))}

        <S.BottomPanel data-cs-capture="">{children}</S.BottomPanel>
      </S.SideNavContainer>
    </>
  );

  return (
    <>
      <S.SideNavBackdrop
        role="presentation"
        suppressHydrationWarning
        $sideNavOpen={sideNavOpened}
        onClick={closeSideNav}
      />

      <S.SideNav
        suppressHydrationWarning
        $sideNavOpen={sideNavOpened}
        id="sideNav"
        ref={sideNavRef}
        $isSearchBoxOpen={isSearchBoxOpen}
        aria-hidden={ariaHiddenAttribute}
      >
        {content}
      </S.SideNav>

      {isSmallScreens && !mobileDialogDisabled ? (
        <CSSTransition
          timeout={250}
          in={sideNavOpened}
          nodeRef={dialogRef}
          classNames="sidenav-dialog"
          unmountOnExit
          mountOnEnter
        >
          <DialogOverlay onDismiss={closeSideNav} initialFocusRef={navElementRef}>
            <S.DialogSidenav ref={dialogRef}>
              <S.DialogSidenavCloseButton
                aria-label={closenSideBar}
                onClick={() => {
                  track({
                    event: 'header_interaction',
                    element: 'nav_toggle',
                    interaction_type: 'close',
                  });
                  closeSideNav();
                }}
              />
              <S.MobileNavWrapper>{content}</S.MobileNavWrapper>
            </S.DialogSidenav>
          </DialogOverlay>
        </CSSTransition>
      ) : null}
    </>
  );
};

export const SideNavigation = memo(SideNavigationComponent, isEqual);
